import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, Snackbar, Alert,
} from '@mui/material';
import CelebrationIcon from '@mui/icons-material/Celebration';
import { DateTime } from 'luxon';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';
import { hapticHeavy } from '../lib/haptics';

/**
 * ANNIVERSARY BANNER
 *
 * Shown on the home page when today's MM-DD matches the user's
 * `join_date` MM-DD and they haven't claimed this year's anniversary
 * bonus yet. Calls `claim_anniversary_bonus` which is idempotent within
 * a calendar year.
 *
 * The bonus scales with years on the platform — 1 day for year 1,
 * +1 day per year, capped at 14 days.
 */

function isAnniversary(joinDateISO?: string | null): boolean {
  if (!joinDateISO) return false;
  const join = DateTime.fromISO(joinDateISO).toUTC();
  const now = DateTime.utc();
  return join.month === now.month && join.day === now.day;
}

function alreadyClaimedThisYear(lastAnnivISO?: string | null): boolean {
  if (!lastAnnivISO) return false;
  const last = DateTime.fromISO(lastAnnivISO).toUTC();
  const now = DateTime.utc();
  return last.year === now.year;
}

function formatDays(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  return `${d} day${d === 1 ? '' : 's'}`;
}

function AnniversaryBanner() {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [eligible, setEligible] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const claimingRef = useRef(false);

  useEffect(() => {
    if (!userRecord) return setEligible(false);
    const isAnniv = isAnniversary(userRecord.join_date);
    const claimedAlready = alreadyClaimedThisYear(
      (userRecord as any).last_anniversary_at,
    );
    setEligible(isAnniv && !claimedAlready);
  }, [userRecord?.id, userRecord?.join_date, (userRecord as any)?.last_anniversary_at]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!userRecord || !eligible || claimed) return null;

  const handleClaim = async () => {
    if (claimingRef.current) return;
    claimingRef.current = true;
    try {
      const { data, error } = await supabase.rpc('claim_anniversary_bonus');
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row?.eligible) {
        setEligible(false);
        return;
      }
      hapticHeavy();
      setToast(`🎂 Happy ${row.years}-year wipe-iversary! +${formatDays(Number(row.granted_seconds))}`);
      setClaimed(true);
    } catch (e: any) {
      console.error('Anniversary claim failed:', e);
      setToast(e.message ?? 'Could not claim anniversary');
    } finally {
      claimingRef.current = false;
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 1.75,
          borderRadius: 2,
          border: '1px solid rgba(255,87,34,0.45)',
          background: (t) => t.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(255,87,34,0.08), rgba(255,193,7,0.04))'
            : 'linear-gradient(135deg, rgba(255,87,34,0.08), rgba(255,193,7,0.04))',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <CelebrationIcon sx={{ color: '#ff5722', fontSize: 28 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"VT323", monospace',
                letterSpacing: 1.5,
                fontSize: '1.05rem',
                color: '#ff5722',
                fontWeight: 700,
                lineHeight: 1.1,
              }}
            >
              ☆ HAPPY WIPE ANNIVERSARY ☆
            </Typography>
            <Typography variant="caption" color="text.secondary">
              You've been wiping for another year. Claim your gift.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={handleClaim}
            sx={{
              bgcolor: '#ff5722',
              color: 'white',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: '#e64a19' },
            }}
          >
            Claim
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AnniversaryBanner;
